import toast from "react-hot-toast";
import type { AxiosError } from "axios";

export const MODEL_UNAVAILABLE_CODE = "MODEL_UNAVAILABLE";
export const MODEL_UNAVAILABLE_MESSAGE =
  "I modelli AI sono attualmente sovraccarichi o non disponibili. Per favore riprova tra qualche minuto.";

const AVAILABILITY_MARKERS = [
  "resource_exhausted",
  "resource exhausted",
  "rate limit",
  "quota",
  "overloaded",
  "too many requests",
  "unavailable",
  "sovraccaric",
  "clienterror: 429",
  "clienterror: 503",
] as const;

type ErrorDetail =
  | string
  | {
      code?: string;
      message?: string;
      status_code?: number;
      msg?: string;
    }
  | Array<{ msg?: string }>;

export interface ParsedApiError {
  message: string;
  isModelUnavailable: boolean;
  statusCode?: number;
}

export class ModelUnavailableApiError extends Error {
  readonly isModelUnavailable = true;
  readonly statusCode?: number;

  constructor(message: string = MODEL_UNAVAILABLE_MESSAGE, statusCode?: number) {
    super(message);
    this.name = "ModelUnavailableApiError";
    this.statusCode = statusCode;
  }
}

function textIndicatesAvailabilityError(text: string): boolean {
  const lowered = text.toLowerCase();
  if (AVAILABILITY_MARKERS.some((marker) => lowered.includes(marker))) {
    return true;
  }
  return /\b429\b/.test(text) || /\b503\b/.test(text);
}

function parseDetailObject(detail: ErrorDetail): ParsedApiError | null {
  if (typeof detail === "string") {
    const isModelUnavailable = textIndicatesAvailabilityError(detail);
    return {
      message: isModelUnavailable ? MODEL_UNAVAILABLE_MESSAGE : detail,
      isModelUnavailable,
    };
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const message =
      typeof first === "object" && first?.msg != null
        ? String(first.msg)
        : String(first);
    const isModelUnavailable = textIndicatesAvailabilityError(message);
    return {
      message: isModelUnavailable ? MODEL_UNAVAILABLE_MESSAGE : message,
      isModelUnavailable,
    };
  }

  if (
    detail &&
    typeof detail === "object" &&
    !Array.isArray(detail)
  ) {
    const code = detail.code;
    const statusCode = detail.status_code;
    const message = detail.message || MODEL_UNAVAILABLE_MESSAGE;
    const isModelUnavailable =
      code === MODEL_UNAVAILABLE_CODE ||
      statusCode === 429 ||
      statusCode === 503 ||
      textIndicatesAvailabilityError(message);
    return {
      message: isModelUnavailable ? MODEL_UNAVAILABLE_MESSAGE : message,
      isModelUnavailable,
      statusCode: statusCode ?? (isModelUnavailable ? 429 : undefined),
    };
  }

  return null;
}

export function parseAxiosError(error: unknown): ParsedApiError {
  const axiosError = error as AxiosError<{ detail?: ErrorDetail }>;
  const statusCode = axiosError.response?.status;
  const detail = axiosError.response?.data?.detail;

  if (statusCode === 429 || statusCode === 503) {
    const parsedDetail = detail != null ? parseDetailObject(detail) : null;
    return {
      message: parsedDetail?.message || MODEL_UNAVAILABLE_MESSAGE,
      isModelUnavailable: true,
      statusCode,
    };
  }

  if (detail != null) {
    const parsedDetail = parseDetailObject(detail);
    if (parsedDetail) {
      return {
        ...parsedDetail,
        statusCode: parsedDetail.statusCode ?? statusCode,
      };
    }
  }

  const fallbackMessage =
    axiosError.message || "Errore durante la comunicazione con il server";
  const isModelUnavailable = textIndicatesAvailabilityError(fallbackMessage);

  return {
    message: isModelUnavailable
      ? MODEL_UNAVAILABLE_MESSAGE
      : fallbackMessage,
    isModelUnavailable,
    statusCode,
  };
}

export function toApiError(error: unknown, fallbackMessage: string): Error {
  const parsed = parseAxiosError(error);
  const message = parsed.message || fallbackMessage;
  if (parsed.isModelUnavailable) {
    return new ModelUnavailableApiError(message, parsed.statusCode);
  }
  return new Error(message);
}

export function notifyModelUnavailable(message?: string): void {
  toast.error(message || MODEL_UNAVAILABLE_MESSAGE, {
    id: "model-unavailable",
    duration: 6000,
  });
}

export function isModelUnavailableError(error: unknown): boolean {
  if (error instanceof ModelUnavailableApiError) {
    return true;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "isModelUnavailable" in error &&
    (error as { isModelUnavailable?: boolean }).isModelUnavailable
  ) {
    return true;
  }
  return parseAxiosError(error).isModelUnavailable;
}

export function isStreamModelUnavailable(
  eventType: string,
  eventData?: Record<string, unknown>
): boolean {
  if (eventType === "model_unavailable") {
    return true;
  }

  if (eventType !== "error") {
    return false;
  }

  const statusCode = eventData?.status_code;
  if (statusCode === 429 || statusCode === 503) {
    return true;
  }

  const message = String(eventData?.message ?? "");
  const errorType = String(eventData?.error_type ?? "");
  return textIndicatesAvailabilityError(`${message} ${errorType}`.trim());
}

export function getStreamErrorContent(isModelUnavailable: boolean): string {
  return isModelUnavailable
    ? MODEL_UNAVAILABLE_MESSAGE
    : "Errore durante la generazione della risposta o modello non disponibile. Riprova tra qualche minuto o contatta l'assistenza.";
}

export function isThrowableModelUnavailable(error: unknown): boolean {
  return isModelUnavailableError(error);
}

export function getThrowableErrorContent(error: unknown): string {
  if (error instanceof Error && error.message) {
    return isModelUnavailableError(error)
      ? MODEL_UNAVAILABLE_MESSAGE
      : error.message;
  }
  const parsed = parseAxiosError(error);
  return parsed.message;
}
